import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { InternalUser } from '@/modules/auth/types/internal_user'
import router from '@/router'
import { http } from '@/common/http/http'

interface LoginResponse {
  access_token: string
  user: {
    id: string
    subGoogle: string
    email: string
    name: string
    picture?: string
  }
}

interface JwtPayload {
  sub: string
  email: string
  name: string
  subGoogle: string
  operations: string[]
  createdAt: string
  updatedAt: string
  iat: number
  exp: number
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const base64Payload = token.split('.')[1]
    const payload = atob(base64Payload)
    return JSON.parse(payload) as JwtPayload
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref<boolean>(false)
  const loggedInUser = ref<InternalUser | null>(null)
  const accessToken = ref<string | null>(null)
  const isInitialized = ref<boolean>(false)
  const operations = ref<string[]>([])

  // Initialize auth state from localStorage
  const initialize = async () => {
    if (isInitialized.value) {
      return // Already initialized
    }
    
    const credential = localStorage.getItem('credential')
    if (credential) {
      try {
        // Fetch user details from backend using stored credential
        const res = await http.post('/admin/auth/login', {
          token: credential,
        })

        const loginResponse = res.data as LoginResponse

        if (loginResponse.access_token) {
          const internalUser: InternalUser = {
            id: loginResponse.user.id,
            subGoogle: loginResponse.user.subGoogle,
            name: loginResponse.user.name,
            email: loginResponse.user.email,
            picture: loginResponse.user.picture || '',
          }

          // Set auth state
          isAuthenticated.value = true
          loggedInUser.value = internalUser
          accessToken.value = loginResponse.access_token

          // Extract operations from JWT
          const payload = decodeJwtPayload(loginResponse.access_token)
          operations.value = payload?.operations || []
        } else {
          // Invalid credential, clear localStorage
          localStorage.removeItem('credential')
        }
      } catch (error) {
        console.error('Failed to initialize auth from stored credential:', error)
        // Clear invalid credential
        localStorage.removeItem('credential')
        isAuthenticated.value = false
        loggedInUser.value = null
        accessToken.value = null
        operations.value = []
      }
    }

    isInitialized.value = true
  }

  // Login with Google credential
  const login = (credential: string, token: string | null, user: InternalUser) => {
    localStorage.setItem('credential', credential)
    isAuthenticated.value = true
    loggedInUser.value = user
    accessToken.value = token

    // Extract operations from JWT
    if (token) {
      const payload = decodeJwtPayload(token)
      operations.value = payload?.operations || []
    }
  }

  // Logout
  const logout = () => {
    localStorage.removeItem('credential')
    isAuthenticated.value = false
    loggedInUser.value = null
    accessToken.value = null
    operations.value = []
    router.push('/login')
  }

  // Check if user has a specific permission
  const hasPermission = (operationId: string): boolean => {
    return operations.value.includes(operationId)
  }

  const getCredential = () => {
    return localStorage.getItem('credential')
  }

  return {
    isAuthenticated,
    isInitialized,
    getCredential,
    loggedInUser,
    accessToken,
    operations,
    initialize,
    login,
    logout,
    hasPermission,
  }
})
